import { SendDelayEntry } from "@/pages/Private/pages/Company/schema/copywritingSettings";
import { SEND_STEP_TYPES } from "@/utils/isSendStep";
import { configuredDelayForStep, DEFAULT_NEW_STEP_DAYS } from "@/utils/stepDelayDefaults";

interface GraphNode {
	id: string;
	type?: string;
}

interface GraphEdge {
	source: string;
	target: string;
}

interface StepPosition {
	/** The node the new step is added after. */
	parentId: string;
	nodes: GraphNode[];
	edges: GraphEdge[];
}

const isOutreachStep = (type: string | undefined): boolean =>
	!!type && (SEND_STEP_TYPES as readonly string[]).includes(type);

/**
 * The step number a new outreach step added after `parentId` gets: 1 plus the outreach steps
 * (email, LinkedIn message, InMail, invite) on the path from the start to its parent. A branch
 * after a condition continues from the same number; visits and conditions are not counted.
 */
export const outreachStepNumber = ({ parentId, nodes, edges }: StepPosition): number => {
	const typeById = new Map(nodes.map((node) => [node.id, node.type]));
	const visited = new Set<string>();
	let count = 0;
	let current: string | undefined = parentId;

	while (current && !visited.has(current)) {
		visited.add(current);

		if (isOutreachStep(typeById.get(current))) {
			count++;
		}

		const nodeId: string = current;

		current = edges.find((edge) => edge.target === nodeId)?.source;
	}

	return count + 1;
};

/**
 * The delay a newly added step starts with (PREC-4299). Both campaign builders — CampaignPage and
 * the Outbox's per-lead CampaignMessagingOverview — call this, so they cannot drift again. An
 * outreach step takes the company's configured delay for its step number; everything else, and an
 * outreach step with nothing configured, gets 1 day. Existing steps are never rewritten.
 */
export const resolveNewStepDays = ({
	nodeType,
	sendDelayDefaults,
	...position
}: StepPosition & {
	nodeType: string;
	sendDelayDefaults: SendDelayEntry[] | null | undefined;
}): number => {
	if (!isOutreachStep(nodeType)) {
		return DEFAULT_NEW_STEP_DAYS;
	}

	return configuredDelayForStep(sendDelayDefaults, outreachStepNumber(position));
};
